import React from 'react';
import { Box, TableContainer, Table, TableBody, Typography, Grid, TableHead } from '@mui/material';
import { useRecoilState } from 'recoil';
import { Styles, ProvisionStyledTableCell, ProvisionStyledTableRow, ProvisionActionStyledTableRow } from 'pages/myidm/style';
import { CancelRequest } from 'pages/myidm/components/index';
import { selectedMembersList, currentSelection, toggleConfirmModel, invalidMember, OrderPayload } from 'pages/myidm/store';
import FormCard from 'pages/myidm/components/form-card';
import MyIdmAccordion from 'pages/myidm/components/accordion';
import OverflowTooltipV2 from 'components/OverflowTooltip/OverflowTooltipV2';
import { PRIVILEGED } from 'pages/myidm/utils/constant';
import { SubmitRequest } from './SubmitRequest';

const membersListStyles = {
    accordionTitle: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%',
        fontSize: '16px',
        px: 1,
        color: '#0047BA',
    },
    groupNameContainer: {
        display: 'flex',
        alignItems: 'center',
    },
    groupNameLabel: {
        fontWeight: 600,
        pr: 0.5,
    },
    groupNameValue: {
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        maxWidth: { xs: '100px', sm: '150px', md: '250px', lg: '350px', xl: '450px' },
    },
    tableHeaderCell: {
        padding: '10px !important',
        backgroundColor: '#E5F5FC !important',
    },
};

const MembersList = () => {
    const [members, setMembers] = useRecoilState(selectedMembersList);
    const [modal, setModal] = useRecoilState(toggleConfirmModel);
    const [current, setCurrent] = useRecoilState(currentSelection);
    const [, setInvalid] = useRecoilState(invalidMember);
    const [order] = useRecoilState(OrderPayload);

    const handleDelete = () => {
        setMembers(() => members.filter((value) => value?.gpid !== current?.gpid));
        setCurrent(null);
        setModal({ ...modal, isOpen: false, message: '', action: false });
        if (members?.length === 1) setInvalid(true);
    };
    React.useEffect(() => {
        if (modal?.action && current?.gpid) {
            handleDelete();
        }
    }, [modal.action]);

    const membersCount = order?.members?.length;
    const getSafeName = (gpid) => {
        let safename = '';
        if (
            !order?.mypamsafeDetails?.map((member) => member?.safeOwner)?.includes(gpid) &&
            (order?.nonSafeDetailsGpid?.includes(gpid) || order?.nonSafeDetailsGpid?.length === 0)
        ) {
            safename = 'SAFE NOT AVAILABLE';
        } else {
            safename = order?.mypamsafeDetails?.filter((member) => member?.safeOwner === gpid)?.[0]?.safeName;
        }

        return safename;
    };

    // Check if we have multiple groups
    const hasMultipleGroups = order?.multipleResourceGroups && order.multipleResourceGroups.length > 0;
    const allGroupsEmpty = hasMultipleGroups && order.multipleResourceGroups.every((g) => !g.members || g.members.length === 0);

    return (
        <Grid item borderRadius={1.5} md={12}>
            <FormCard title="Add Members" gap={0} padding={2} margin={2}>
                <Box sx={{ mt: 2, width: '100%' }}>
                    {hasMultipleGroups ? (
                        /* Multiple Groups Display */
                        <>
                            {allGroupsEmpty ? (
                                <TableContainer sx={Styles?.ProvisionAddMemberForm?.table.tableWrapper}>
                                    <Table sx={{ ...Styles?.ProvisionAddMemberForm?.table?.main, height: 'auto' }} size="small" stickyHeader>
                                        <TableBody>
                                            <ProvisionActionStyledTableRow>
                                                <ProvisionStyledTableCell>No members selected.</ProvisionStyledTableCell>
                                            </ProvisionActionStyledTableRow>
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            ) : (
                                order.multipleResourceGroups.map((group, idx) =>
                                    group?.groupName ? (
                                        <MyIdmAccordion
                                            key={`resource-group-section-${idx}`}
                                            title={
                                                <Box sx={membersListStyles.accordionTitle}>
                                                    <Box sx={membersListStyles.groupNameContainer}>
                                                        <Typography sx={membersListStyles.groupNameLabel}>Group Name: </Typography>
                                                        <OverflowTooltipV2 component="span" sx={membersListStyles.groupNameValue}>
                                                            {group?.groupName || '-'}
                                                        </OverflowTooltipV2>
                                                    </Box>
                                                    {group?.members?.length ? <Box>{group?.members?.length} Member(s) selected</Box> : null}
                                                </Box>
                                            }
                                        >
                                            <TableContainer sx={Styles?.ProvisionAddMemberForm?.table.tableWrapper}>
                                                <Table sx={{ ...Styles?.ProvisionAddMemberForm?.table?.main, height: 'auto' }} size="small" stickyHeader>
                                                    <TableHead>
                                                        <ProvisionActionStyledTableRow>
                                                            <ProvisionStyledTableCell sx={membersListStyles.tableHeaderCell}>GPID</ProvisionStyledTableCell>
                                                            <ProvisionStyledTableCell sx={membersListStyles.tableHeaderCell}>
                                                                Full Name
                                                            </ProvisionStyledTableCell>
                                                            <ProvisionStyledTableCell sx={membersListStyles.tableHeaderCell}>
                                                                {order?.adGroupType !== 'Privileged' ? 'Email ID' : 'myPAM Safe'}
                                                            </ProvisionStyledTableCell>
                                                            {group?.activeDirectoryGroupType === PRIVILEGED && (
                                                                <ProvisionStyledTableCell sx={membersListStyles.tableHeaderCell}>
                                                                    Secondary Account
                                                                </ProvisionStyledTableCell>
                                                            )}
                                                        </ProvisionActionStyledTableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {group?.members?.length > 0 ? (
                                                            group.members.map((member) => (
                                                                <ProvisionStyledTableRow
                                                                    className="animate-opacity"
                                                                    key={member?.gpid}
                                                                    data-testId="members-list-table"
                                                                >
                                                                    <ProvisionStyledTableCell>{member?.gpid || ''}</ProvisionStyledTableCell>
                                                                    <ProvisionStyledTableCell>{member?.name || '-'}</ProvisionStyledTableCell>
                                                                    <ProvisionStyledTableCell>
                                                                        {order?.adGroupType !== 'Privileged' ? member?.email || '-' : getSafeName(member?.gpid)}
                                                                    </ProvisionStyledTableCell>
                                                                    {group?.activeDirectoryGroupType === PRIVILEGED && (
                                                                        <ProvisionStyledTableCell>
                                                                            {member?.secondaryAccount?.secondaryAccountName || '-'}
                                                                        </ProvisionStyledTableCell>
                                                                    )}
                                                                </ProvisionStyledTableRow>
                                                            ))
                                                        ) : (
                                                            <ProvisionStyledTableRow>
                                                                <ProvisionStyledTableCell colSpan={group?.activeDirectoryGroupType === PRIVILEGED ? 4 : 3}>
                                                                    No members selected.
                                                                </ProvisionStyledTableCell>
                                                            </ProvisionStyledTableRow>
                                                        )}
                                                    </TableBody>
                                                </Table>
                                            </TableContainer>
                                        </MyIdmAccordion>
                                    ) : null
                                )
                            )}
                            {/* Show action buttons only if every group has at least one member */}
                            {order?.multipleResourceGroups?.length > 0 &&
                                order.multipleResourceGroups.every((g) => Array.isArray(g.members) && g.members.length > 0) && (
                                    <Box display="flex" gap={1} sx={{ mb: 2, alignItems: 'baseline', justifyContent: 'flex-end', width: '100%' }}>
                                        <Box sx={{ marginTop: '0px' }}>
                                            <CancelRequest />
                                        </Box>
                                        <SubmitRequest />
                                    </Box>
                                )}
                        </>
                    ) : (
                        /* Single Group Display (Original) */
                        <Box data-testid="approval-list" sx={{ width: '100%', marginTop: 2 }}>
                            <TableContainer sx={Styles?.ProvisionAddMemberForm?.table.tableWrapper}>
                                <Table sx={{ ...Styles?.ProvisionAddMemberForm?.table?.main, height: 'auto' }} size="small" stickyHeader>
                                    {membersCount > 0 && (
                                        <TableHead>
                                            <ProvisionActionStyledTableRow>
                                                <ProvisionStyledTableCell sx={membersListStyles.tableHeaderCell}>GPID</ProvisionStyledTableCell>
                                                <ProvisionStyledTableCell sx={membersListStyles.tableHeaderCell}>Full Name</ProvisionStyledTableCell>
                                                <ProvisionStyledTableCell sx={membersListStyles.tableHeaderCell}>
                                                    {order?.adGroupType !== 'Privileged' ? 'Email ID' : 'myPAM Safe'}
                                                </ProvisionStyledTableCell>
                                                {order?.adGroupType === PRIVILEGED && (
                                                    <ProvisionStyledTableCell sx={membersListStyles.tableHeaderCell}>
                                                        Secondary Account
                                                    </ProvisionStyledTableCell>
                                                )}
                                            </ProvisionActionStyledTableRow>
                                        </TableHead>
                                    )}
                                    <TableBody sx={{ marginTop: 4 }}>
                                        <ProvisionActionStyledTableRow>
                                            {(membersCount < 1 || membersCount === undefined) && (
                                                <ProvisionStyledTableCell>No members selected.</ProvisionStyledTableCell>
                                            )}
                                        </ProvisionActionStyledTableRow>
                                        {membersCount > 0 ? (
                                            <>
                                                {order?.members?.length > 0 &&
                                                    order?.members?.map((member) => (
                                                        <ProvisionStyledTableRow
                                                            className="animate-opacity"
                                                            key={member?.gpid}
                                                            data-testId="members-list-table"
                                                        >
                                                            <ProvisionStyledTableCell>{member?.gpid || ''}</ProvisionStyledTableCell>
                                                            <ProvisionStyledTableCell>{member?.name || '-'}</ProvisionStyledTableCell>
                                                            <ProvisionStyledTableCell>
                                                                {order?.adGroupType !== 'Privileged' ? member?.email : getSafeName(member?.gpid)}
                                                            </ProvisionStyledTableCell>
                                                            {order?.adGroupType === PRIVILEGED && (
                                                                <ProvisionStyledTableCell>
                                                                    {member?.secondaryAccount?.secondaryAccountName || '-'}
                                                                </ProvisionStyledTableCell>
                                                            )}
                                                        </ProvisionStyledTableRow>
                                                    ))}
                                            </>
                                        ) : null}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            {(membersCount > 0 && order?.adGroupType !== 'Privileged' && order?.estimatedCost?.details?.length === 0) ||
                            (order?.adGroupType === 'Privileged' && order?.nonSafeDetailsGpid?.length === 0) ? (
                                <Box display="flex" id="test" alignItems="baseline" justifyContent="space-between">
                                    <Typography>
                                        <b>{membersCount}</b> {membersCount < 2 ? 'Member' : 'Members'} Selected{' '}
                                    </Typography>
                                    <Box display="flex" flexDirection="row-reverse" gap={1} sx={{ mt: 2, alignItems: 'baseline' }}>
                                        <Box sx={{ marginTop: '0px' }}>
                                            <CancelRequest />
                                        </Box>
                                        <SubmitRequest />
                                    </Box>
                                </Box>
                            ) : null}
                        </Box>
                    )}
                </Box>
            </FormCard>
        </Grid>
    );
};

export default MembersList;
